import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/shared/api/client';
import { useAuth } from './store';

/**
 * A 401 is the server saying "you are not signed in" — final, retrying it is
 * pointless. Anything else (a 500, a dropped connection, a dev server busy
 * with the dozen other calls the dashboard fires at once) is transient, and
 * treating it as a sign-out is how a working session disappears a second
 * after logging in.
 */
function isDefinitiveAuthFailure(error: unknown): boolean {
  const status = (error as AxiosError | undefined)?.response?.status;
  return status === 401 || status === 403;
}

interface MeResponse {
  data: {
    user: {
      id: number;
      name: string;
      email: string;
      locale: string;
      timezone: string;
      avatar_path?: string | null;
    };
    permissions: string[];
    roles: string[];
    tenant: {
      id: string | null;
      enabled: string[];
    };
  };
}

export function useMe() {
  const setSession = useAuth((s) => s.setSession);
  const currentTenantId = useAuth((s) => s.currentTenantId);
  const tenants = useAuth((s) => s.tenants);

  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.get<MeResponse>('/api/v1/me');
      setSession({
        user: data.data.user,
        tenants: tenants,
        currentTenantId: data.data.tenant.id ?? currentTenantId,
        permissions: data.data.permissions,
        roles: data.data.roles,
        enabledModules: data.data.tenant.enabled,
      });
      return data.data;
    },
    retry: (failureCount, error) => !isDefinitiveAuthFailure(error) && failureCount < 3,
    retryDelay: (attempt) => Math.min(400 * 2 ** attempt, 3000),
  });
}

export { isDefinitiveAuthFailure };
